import NativeAd from "../../ads/Native";
import styles from "./ArticleRecirc.module.scss";
import cx from "classnames";
import { ArticleRecircAuthorFragment, ArticleRecircFragment } from "types/api";
import gql from "graphql-tag";

const AuthorWrapper = ({
  articleIndex,
  displayName,
  url,
  heading,
}: {
  articleIndex: number;
  displayName: string;
  url: string;
  heading: string;
}) => {
  return (
    <a
      className={styles.itemLink}
      href={url}
      data-action={`click link - ${heading.toLowerCase()} - author ${
        articleIndex + 1
      }`}
      data-label={url}
    >
      <span dangerouslySetInnerHTML={{ __html: displayName }} />
    </a>
  );
};

interface AuthorComponentProps {
  articleIndex: number;
  displayName: string;
  url: string;
  heading: string;
}

function* createAuthors(
  authors: ArticleRecircAuthorFragment[],
  AuthorComponent: (props: AuthorComponentProps) => JSX.Element,
  articleIndex: number,
  headingDisplayName: string
) {
  let i = 0;
  while (i < authors.length) {
    const author = authors[i];

    if (authors.length > 1 && i === authors.length - 1) {
      yield " and ";
    }

    if (author) {
      yield (
        <AuthorComponent
          {...author}
          articleIndex={articleIndex}
          key={author.id}
          heading={headingDisplayName}
        />
      );
    }

    if (authors.length > 2 && i !== authors.length - 1) {
      yield ", ";
    }

    i += 1;
  }
}

const RecircNativeAd = () => {
  return (
    <NativeAd
      targetingPos="native-article-related"
      targetingNative="native"
      className="s-native s-native--short-title s-native--standard s-native--small"
    />
  );
};

const ArticleRecirc = ({
  displayName,
  articles,
  showImages,
  position,
}: {
  displayName: string;
  articles: ArticleRecircFragment;
  showImages: boolean;
  position: string;
}) => {
  const edges =
    articles.edges.length > 3 ? articles.edges.slice(0, 3) : articles.edges;
  return (
    <section className={cx(styles.root, styles[position])}>
      <h2 className={styles.heading}>{displayName}</h2>
      <ul className={styles.list}>
        {edges.slice(0, 3).map((article, i) => {
          return (
            <li key={`article-recirc-rr-${i}`}>
              {/* For rail right module, insert native ad before last article so the last article can be hidden with .ad-loaded + .itemRoot if an ad loads */}
              {position === "inline" && i === edges.length - 1 && (
                <RecircNativeAd />
              )}
              <div className={styles.itemRoot}>
                {article.node.riverImage && showImages && (
                  <figure className={styles.itemPhoto}>
                    <a
                      className={styles.itemLink}
                      href={article.node.url}
                      title={`Read More: ${article.node.title}`}
                      data-action={`click link - ${displayName.toLowerCase()} - image ${
                        i + 1
                      }`}
                      data-label={article.node.url}
                    >
                      <picture className={styles.itemMedia}>
                        <img
                          className={styles.itemImage}
                          alt={article.node.riverImage?.altText || ""}
                          src={article.node.riverImage?.url}
                          srcSet={article.node.riverImage?.srcSet}
                        />
                      </picture>
                    </a>
                  </figure>
                )}
                <div className={styles.itemContent}>
                  {article.node.title && (
                    <h3 className={styles.itemTitle}>
                      <a
                        className={styles.itemLink}
                        href={article.node.url}
                        dangerouslySetInnerHTML={{
                          __html: article.node.title,
                        }}
                        data-action={`click link - ${displayName.toLowerCase()} - title ${
                          i + 1
                        }`}
                        data-label={article.node.url}
                      ></a>
                    </h3>
                  )}
                  {article.node.authors && (
                    <address className={styles.itemByline}>
                      {Array.from(
                        createAuthors(
                          article.node.authors,
                          AuthorWrapper,
                          i,
                          displayName
                        )
                      )}
                    </address>
                  )}
                </div>
              </div>
            </li>
          );
        })}
        {/* For rail right module, append native ad after last article */}
        {position === "rightRail" && (
          <li>
            <RecircNativeAd />
          </li>
        )}
      </ul>
    </section>
  );
};

ArticleRecirc.fragment = gql`
  fragment articleRecircAuthor on Author {
    url
    displayName
    id
    slug
  }

  fragment articleRecirc on RiverConnection {
    edges {
      node {
        url
        title
        authors {
          ...articleRecircAuthor
        }
        riverImage(width: 80, height: 80, sizes: ["1x", "2x"]) {
          id
          url
          srcSet
          width
          height
          altText
        }
      }
    }
  }
`;

export default ArticleRecirc;
